import React, { useEffect, useState } from "react";
import { CalendarDays, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/api";
import { formatApiErrorDetail } from "../../../lib/constants";

export default function AdminMarketDays() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ orders_open: "", orders_close: "", delivery_date: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/market-days").then((r) => setDays(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const active = days.find((d) => d.is_active);
  const isPast = (d) => d && new Date(d.orders_close) < new Date();

  const ukWallClockToISO = (wall) => {
    // wall: "YYYY-MM-DDTHH:mm" entered as UK local time -> UTC instant
    const guess = new Date(wall + ":00Z");
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/London", hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const p = dtf.formatToParts(guess).reduce((a, x) => ((a[x.type] = x.value), a), {});
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    const offsetMin = (asUTC - guess.getTime()) / 60000;
    return new Date(guess.getTime() - offsetMin * 60000).toISOString();
  };

  const create = async () => {
    if (!form.orders_open || !form.orders_close || !form.delivery_date) {
      toast.error("All date fields are required");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/admin/market-days", {
        orders_open: ukWallClockToISO(form.orders_open),
        orders_close: ukWallClockToISO(form.orders_close),
        delivery_date: ukWallClockToISO(form.delivery_date),
      });
      toast.success("Market day opened");
      setForm({ orders_open: "", orders_close: "", delivery_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    await adminApi.put(`/admin/market-days/${id}`, { is_active: false });
    toast.success("Market day closed");
    load();
  };

  const fmt = (d) => new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
  const inp = "w-full h-11 px-3 rounded-md bg-gray-100 focus:bg-white border border-gray-200 focus:border-[#228B22] outline-none transition-colors text-sm";

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        {/* Current status */}
        <div className={`rounded-lg p-5 mb-5 ${active ? "bg-[#1A4331] text-white" : "bg-slate-700 text-white"}`}>
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={18} />
            <span className="text-sm font-semibold uppercase tracking-wide opacity-80">Current Market Day</span>
          </div>
          {active ? (
            <div className="text-sm space-y-1">
              <p>Opens: <b>{fmt(active.orders_open)}</b></p>
              <p>Closes: <b>{fmt(active.orders_close)}</b></p>
              <p>Delivery: <b>{fmt(active.delivery_date)}</b></p>
              {isPast(active) && (
                <p data-testid="market-day-time-passed" className="mt-2 block w-fit rounded bg-amber-400 text-[#0A1F13] px-2 py-0.5 text-xs font-semibold">Closing time has passed: customers can no longer order</p>
              )}
              <button data-testid="close-market-day" onClick={() => deactivate(active.id)} className="mt-3 px-4 h-9 rounded-md bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors">Close Market Day</button>
            </div>
          ) : (
            <p className="text-sm opacity-80">No active market day. Create one below to open orders.</p>
          )}
        </div>

        {/* Create form */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-bold text-[#0A1F13] mb-4">Open New Market Day <span className="text-xs font-normal text-gray-400">(all times UK / BST)</span></h3>
          <div className="space-y-3">
            <div><label className="text-sm text-gray-500 mb-1 block">Orders Open</label><input data-testid="market-open-input" type="datetime-local" value={form.orders_open} onChange={(e) => setForm({ ...form, orders_open: e.target.value })} className={inp} /></div>
            <div><label className="text-sm text-gray-500 mb-1 block">Orders Close</label><input data-testid="market-close-input" type="datetime-local" value={form.orders_close} onChange={(e) => setForm({ ...form, orders_close: e.target.value })} className={inp} /></div>
            <div><label className="text-sm text-gray-500 mb-1 block">Delivery Date</label><input data-testid="market-delivery-input" type="datetime-local" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className={inp} /></div>
            <button data-testid="create-market-day" onClick={create} disabled={saving} className="w-full h-11 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Open Market Day"}
            </button>
            <p className="text-xs text-gray-400">Opening a new market day automatically closes any previous one.</p>
          </div>
        </div>
      </div>

      {/* History */}
      <div>
        <h3 className="font-bold text-[#0A1F13] mb-3">History</h3>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#228B22]" size={24} /></div>
        ) : (
          <div className="space-y-3">
            {days.map((d) => (
              <div key={d.id} data-testid={`market-day-${d.id}`} className="bg-white rounded-lg border border-gray-200 p-4 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-[#0A1F13]">{fmt(d.orders_open)}</span>
                  {d.is_active && isPast(d) ? (
                    <span className="text-xs font-semibold text-amber-600">Closed (time passed)</span>
                  ) : d.is_active ? (
                    <span className="flex items-center gap-1 text-xs font-semibold text-[#166534]"><CheckCircle2 size={13} /> Active</span>
                  ) : (
                    <span className="text-xs text-gray-400">Closed</span>
                  )}
                </div>
                <p className="text-gray-500">Closes {fmt(d.orders_close)}</p>
                <p className="text-gray-500">Delivery {fmt(d.delivery_date)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
